import { useCallback, useRef } from 'react'

import { findLastFocusableNode } from 'hsds/utils/focus'

import type {
  UseModalAutoFocusProps,
  UseModalAutoFocusReturn,
} from './SimpleModal.types'

export function allowGlobalHotkeys(target: HTMLElement): boolean {
  if (target.dataset.blocksGlobalHotkeys) {
    return false
  }

  if (target.closest('[data-blocks-global-hotkeys="true"]')) {
    return false
  }

  return true
}

export function shouldUnsetDimensions(
  unsetDimensions?: boolean,
  layoutName?: string
): boolean {
  if (layoutName === 'Confirmation') return true
  if (unsetDimensions) return true

  return false
}

/**
 * Move the current focus to a modal children element:
 *
 * - If a target element is specified, the focus should be placed on that element
 * - If no target element, the focus should be placed on the footer primary button if it exists
 * - if no footer primary button, the autofocus should be placed on the first primary node
 * - if no primary button, the autofocus should be placed on the first focusable node
 * - if no focusable node, the modal body should be focuses
 * - autoFocus could be turned off entirely, allowing an element's autofocus to be used instead (ie: input)
 */
export const useModalAutoFocus = ({
  shouldRender,
  focusTargetElement,
  focusModalOnShow,
}: UseModalAutoFocusProps): UseModalAutoFocusReturn => {
  const modalRef = useRef<HTMLElement | null>(null)

  const getNextFocusableNode = useCallback(() => {
    modalRef.current?.focus({ focusVisible: true })

    if (focusTargetElement?.current) {
      return focusTargetElement.current
    }

    if (!modalRef.current) return null

    const primaryFooterAction = modalRef.current.querySelector<HTMLElement>(
      '.SimpleModal__footer .c-Button.is-color-blue:last-child'
    )

    if (primaryFooterAction) {
      return primaryFooterAction
    }

    const anyPrimaryFooterAction = modalRef.current.querySelector<HTMLElement>(
      '.c-Button.is-color-blue'
    )

    if (anyPrimaryFooterAction) {
      return anyPrimaryFooterAction
    }

    const node = findLastFocusableNode(modalRef.current) as HTMLElement | null

    if (node) {
      return node
    }

    return modalRef.current
  }, [focusTargetElement])

  const handleAutoFocus = useCallback(() => {
    if (!modalRef.current) return

    // Store current scroll position of modal body
    const modalBody = modalRef.current.querySelector('.SimpleModal__body')
    const node = getNextFocusableNode()

    if (node) {
      if (node.classList.contains('c-Button')) {
        node.classList.add('is-focused')
      }
      node.focus({ preventScroll: true }) // Prevent automatic scrolling
    }

    // Reset scroll position after focus
    if (modalBody) {
      modalBody.scrollTop = 0
    }
  }, [getNextFocusableNode])

  const setModalRef = useCallback(
    (modalElement: HTMLElement | null) => {
      modalRef.current = modalElement
      if (shouldRender && focusModalOnShow) {
        handleAutoFocus()
      }
    },
    [shouldRender, focusModalOnShow, handleAutoFocus]
  )

  return [setModalRef, modalRef]
}

export function getFocusableElement(
  element: HTMLElement | null
): HTMLElement | null {
  if (!element) return null

  const selector =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  return element.querySelector(selector)
}

export function checkFocusWithin(element: HTMLElement): boolean {
  return element.contains(document.activeElement)
}
